import React from 'react';
import { Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useActivityAudio from '../../../../../../hooks/use-activity-audio';
import useOverlay from '../../../../../../hooks/use-overlay';
import { useFindTheLetter } from '../../../../../../hooks/use-find-the-letter';
import useHint from '../../../../../../hooks/use-hint';
import { FONT_GSS_FUTURA_SEMI_BOLD } from '../../../../../../themes/defaultTheme';
import { GameSounds } from '../../../../../../transformers/FindTheLetter';
import genericSounds from '../../../../../../transformers/genericSounds';
import { LetterProps, LetterConstants } from '../../../../../../types';
import {
  isCorrectLetter,
  isFoundLetterCorrect,
  isFoundLetterWrong,
  letterIsHint,
} from '../../../../../../util/find-the-letter-util';

const Letter: React.FC<LetterProps> = ({ page, letter, letterId, activityLetter }) => {
  const [{ pages, hint }, { setFoundLetters }] = useFindTheLetter();
  const [, { playSound }] = useActivityAudio();
  const [{ showHint }] = useHint();
  const [{ showOverlay }] = useOverlay();

  const classes = useStyles();

  const letterWasPreviouslySelected =
    isFoundLetterCorrect(pages[page], letterId) || isFoundLetterWrong(pages[page], letterId);

  const isHint = letterIsHint(letterId, page, hint);

  const handleLetterClick = () => {
    if (showHint) return;
    if (letterWasPreviouslySelected) return;

    if (isCorrectLetter(activityLetter, letter)) {
      setFoundLetters(page, { letterId, value: LetterConstants.CORRECT });
      playSound(GameSounds.LETTER_AUDIO(activityLetter.toLowerCase()));
    } else {
      setFoundLetters(page, { letterId, value: LetterConstants.WRONG });
      playSound(genericSounds.BOINK);
    }
  };

  return (
    <Button
      key={letterId}
      className={`${classes.letter} ${showHint && isHint ? classes.hint : ''} ${
        isFoundLetterCorrect(pages[page], letterId) ? classes.success : ''
      } ${isFoundLetterWrong(pages[page], letterId) ? classes.wrong : ''}`}
      disabled={
        (showHint && !letterWasPreviouslySelected && !isHint) || showOverlay || letter === ' '
      }
      onClick={() => handleLetterClick()}
    >
      {letter}
    </Button>
  );
};

const useStyles = makeStyles((theme) => ({
  letter: {
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    fontSize: `clamp(2rem, 3vw, 3.5rem)`,
    lineHeight: `clamp(2.5rem, 4vw, 4.5rem)`,
    '@media (orientation: portrait)': {
      fontSize: `clamp(2.5rem, 5vh, 3.5rem)`,
      lineHeight: `clamp(3rem, 7vh, 4.5rem)`,
    },
    padding: 0,
    minWidth: 10,
    color: theme.palette.common.black,
    fontWeight: 'bold',
    paddingRight: theme.spacing(0.5),
    transition: 'all .2s ease-in',
  },
  success: {
    textDecoration: 'underline',
    color: `${theme.palette.primary.main}`,
  },
  wrong: {
    color: `${theme.palette.error.main}`,
  },
  hint: {
    textDecoration: 'underline',
    cursor: 'default',
    '&:hover': {
      background: 'transparent',
      textDecoration: 'underline',
    },
  },
}));

export default Letter;
